import { Suspense } from "react"
import { RouteErrorBoundary } from "@/components/common/error-page/route-error-boundary"
import { AdminRouteGuard } from "@/features/admin/components"
import { lazyWithRetry } from "@/lib/lazy-with-retry"
import { initTracking, trackPageView } from "@/lib/tracking/analytics"
import { trackFbPageView } from "@/lib/tracking/facebook-pixel"
// Layouts - keep eager (shells that render immediately)
import AuthLayout from "@/routes/auth/AuthLayout"
import MainLayout from "@/routes/main/MainLayout"
import OnboardingLayout from "@/routes/onboarding/layout"
import { createBrowserRouter, LoaderFunction, Navigate } from "react-router-dom"

// Admin layouts - keep eager (structural components)
const UserDetailLayout = lazyWithRetry(() =>
  import("@/features/admin/components").then(m => ({ default: m.UserDetailLayout }))
)
const AgentDetailLayout = lazyWithRetry(() =>
  import("@/features/admin/components").then(m => ({ default: m.AgentDetailLayout }))
)

// Auth routes
const LoginRoute = lazyWithRetry(() => import("@/routes/auth/login/LoginRoute"))
const RegisterRoute = lazyWithRetry(() => import("@/routes/auth/register/RegisterRoute"))
const ForgotPasswordRoute = lazyWithRetry(
  () => import("@/routes/auth/forgot-password/ForgotPasswordRoute")
)
const ResetPasswordRoute = lazyWithRetry(
  () => import("@/routes/auth/reset-password/ResetPasswordRoute")
)
const EmailVerificationRoute = lazyWithRetry(
  () => import("@/routes/auth/email-verification/EmailVerificationRoute")
)
const FacebookCallbackRoute = lazyWithRetry(
  () => import("@/routes/auth/facebook/FacebookCallbackRoute")
)
const GoogleCallbackRoute = lazyWithRetry(() => import("@/routes/auth/google/GoogleCallbackRoute"))
const InstagramCallbackRoute = lazyWithRetry(
  () => import("@/routes/auth/instagram/InstagramCallbackRoute")
)

// Onboarding routes
const CompanyDetailsRoute = lazyWithRetry(() =>
  import("@/routes/onboarding/company-details-route").then(m => ({
    default: m.CompanyDetailsRoute,
  }))
)
const OnboardingPage = lazyWithRetry(() => import("@/routes/onboarding/page"))

// Main routes
const DashboardRoute = lazyWithRetry(() => import("@/routes/main/dashboard/DashboardRoute"))
const CalendarRoute = lazyWithRetry(() => import("@/routes/main/calendar/CalendarRoute"))
const ChatRoute = lazyWithRetry(() => import("@/routes/main/chat/ChatRoute"))
const ContactsRoute = lazyWithRetry(() => import("@/routes/main/contacts/ContactsRoute"))
const ContactDetailRoute = lazyWithRetry(() => import("@/routes/main/contacts/ContactDetailRoute"))
const GroupsRoute = lazyWithRetry(() => import("@/routes/main/groups/GroupsRoute"))
const GroupDetailRoute = lazyWithRetry(() => import("@/routes/main/groups/GroupDetailRoute"))
const GroupTeamRoute = lazyWithRetry(() => import("@/routes/main/groups/GroupTeamRoute"))
const GroupRolesRoute = lazyWithRetry(() => import("@/routes/main/groups/GroupRolesRoute"))
const GroupCategoriesRoute = lazyWithRetry(
  () => import("@/routes/main/groups/GroupCategoriesRoute")
)
const GroupsMetricsRoute = lazyWithRetry(() => import("@/routes/main/groups/GroupsMetricsRoute"))
const AgentsRoute = lazyWithRetry(() => import("@/routes/main/agents/page"))
const AgentFormRoute = lazyWithRetry(() => import("@/routes/main/agents/new/page"))
const AgentEditRoute = lazyWithRetry(() => import("@/routes/main/agents/[:id]/page"))
const AvailabilityRoute = lazyWithRetry(() =>
  import("@/routes/main/availability/AvailabilityRoute").then(m => ({
    default: m.AvailabilityRoute,
  }))
)
const ReportsRoute = lazyWithRetry(() =>
  import("@/routes/reports/reports-route").then(m => ({ default: m.ReportsRoute }))
)
const ReportsV2Route = lazyWithRetry(() =>
  import("@/routes/reports/reports-v2-route").then(m => ({ default: m.ReportsV2Route }))
)
const InsightsRoute = lazyWithRetry(() => import("@/routes/main/insights/page"))
const IntegrationsRoute = lazyWithRetry(
  () => import("@/routes/main/integrations/IntegrationsRoute")
)
const GoogleCalendarConfigPage = lazyWithRetry(
  () => import("@/routes/main/integrations/google-calendar/page")
)
const GoogleCalendarCallbackRoute = lazyWithRetry(
  () => import("@/routes/main/integrations/GoogleCalendarCallbackRoute")
)
const WebhooksPage = lazyWithRetry(() => import("@/routes/main/integrations/webhooks/page"))
const WebhookNewPage = lazyWithRetry(() => import("@/routes/main/integrations/webhooks/new/page"))
const WebhookDetailPage = lazyWithRetry(
  () => import("@/routes/main/integrations/webhooks/[id]/page")
)
const GenericIntegrationsPage = lazyWithRetry(
  () => import("@/routes/main/integrations/generic/page")
)
const GenericIntegrationNewPage = lazyWithRetry(
  () => import("@/routes/main/integrations/generic/new/page")
)
const GenericIntegrationDetailPage = lazyWithRetry(
  () => import("@/routes/main/integrations/generic/[id]/page")
)
const SettingsPage = lazyWithRetry(() => import("@/routes/main/settings/page"))

// Leads routes
const LeadsLayoutRoute = lazyWithRetry(() => import("@/routes/main/leads/LeadsLayoutRoute"))
const LeadsRoute = lazyWithRetry(() => import("@/routes/main/leads/LeadsRoute"))
const CrmIntegrationsRoute = lazyWithRetry(() => import("@/routes/main/leads/CrmIntegrationsRoute"))
const CrmReportsRoute = lazyWithRetry(() => import("@/routes/main/leads/CrmReportsRoute"))

// Automations routes
const AutomationsRoute = lazyWithRetry(() => import("@/routes/main/automations/AutomationsRoute"))
const AutomationNewRoute = lazyWithRetry(
  () => import("@/routes/main/automations/AutomationNewRoute")
)
const AutomationEditRoute = lazyWithRetry(
  () => import("@/routes/main/automations/AutomationEditRoute")
)
const AutomationFlowRoute = lazyWithRetry(
  () => import("@/routes/main/automations/AutomationFlowRoute")
)

// Media Library routes
const MediaLibraryRoute = lazyWithRetry(() => import("@/routes/main/media-library/page"))

// Qualification Forms routes
const QualificationFormsRoute = lazyWithRetry(
  () => import("@/routes/main/qualification-forms/page")
)
const QualificationFormEditorRoute = lazyWithRetry(
  () => import("@/routes/main/qualification-forms/editor-page")
)

// Attribute Groups routes
const AttributeGroupsRoute = lazyWithRetry(() => import("@/routes/main/attribute-groups/page"))
const AttributeGroupEditorRoute = lazyWithRetry(
  () => import("@/routes/main/attribute-groups/editor-page")
)

// Plugins routes
const PluginsRoute = lazyWithRetry(() => import("@/routes/main/plugins/page"))
const PublicApiPluginRoute = lazyWithRetry(() => import("@/routes/main/plugins/public-api-page"))

// Payment Links routes
const PaymentLinksRoute = lazyWithRetry(() => import("@/routes/main/payment-links/page"))

// Commerce routes
const CommerceCatalogsRoute = lazyWithRetry(() => import("@/routes/main/commerce/catalogos"))
const CommerceOrdersRoute = lazyWithRetry(() => import("@/routes/main/commerce/pedidos"))

// Schedulers routes
const SchedulersRoute = lazyWithRetry(() => import("@/routes/main/schedulers/page"))

// Broadcast routes
const BroadcastListRoute = lazyWithRetry(() => import("@/routes/main/broadcast/BroadcastListRoute"))
const BroadcastNewRoute = lazyWithRetry(() => import("@/routes/main/broadcast/BroadcastNewRoute"))
const BroadcastEditRoute = lazyWithRetry(() => import("@/routes/main/broadcast/BroadcastEditRoute"))
const BroadcastDashboardRoute = lazyWithRetry(
  () => import("@/routes/main/broadcast/BroadcastDashboardRoute")
)

// Public routes
const SharedQrPage = lazyWithRetry(() => import("@/routes/shared/shared-qr-page"))

// Admin routes
const AdminDashboardRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-dashboard-route").then(m => ({
    default: m.AdminDashboardRoute,
  }))
)
const AdminUsersListRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-users-list-route").then(m => ({
    default: m.AdminUsersListRoute,
  }))
)
const AdminPromptsRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-prompts-route").then(m => ({ default: m.AdminPromptsRoute }))
)
const AdminPromptDetailRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-prompt-detail-route").then(m => ({
    default: m.AdminPromptDetailRoute,
  }))
)
const AdminBillingRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-billing-route").then(m => ({ default: m.AdminBillingRoute }))
)
const AdminSettingsRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-settings-route").then(m => ({ default: m.AdminSettingsRoute }))
)
const AdminMarketingRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-marketing-route").then(m => ({
    default: m.AdminMarketingRoute,
  }))
)
const AdminConversationsRoute = lazyWithRetry(
  () => import("@/routes/admin/admin-conversations-route")
)
const AdminConversationTimelineRoute = lazyWithRetry(
  () => import("@/routes/admin/admin-conversation-timeline-route")
)
const AdminTechProvidersRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-tech-providers-route").then(m => ({
    default: m.AdminTechProvidersRoute,
  }))
)
const AdminLibraryRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-library-route").then(m => ({
    default: m.AdminLibraryRoute,
  }))
)
const AdminLLMModelsRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-llm-models-route").then(m => ({
    default: m.AdminLLMModelsRoute,
  }))
)
const AdminToolsRoute = lazyWithRetry(() =>
  import("@/routes/admin/admin-tools-route").then(m => ({
    default: m.AdminToolsRoute,
  }))
)

// Admin user tabs
const UserOverviewTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-overview-tab").then(m => ({ default: m.UserOverviewTab }))
)
const UserAgentsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-agents-tab").then(m => ({ default: m.UserAgentsTab }))
)
const UserLeadsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-leads-tab").then(m => ({ default: m.UserLeadsTab }))
)
const UserAppointmentsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-appointments-tab").then(m => ({
    default: m.UserAppointmentsTab,
  }))
)
const UserProvidersTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-providers-tab").then(m => ({ default: m.UserProvidersTab }))
)
const UserCommunicationTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-communication-tab").then(m => ({
    default: m.UserCommunicationTab,
  }))
)
const UserActivitiesTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-activities-tab").then(m => ({
    default: m.UserActivitiesTab,
  }))
)
const UserIntegrationsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-integrations-tab").then(m => ({
    default: m.UserIntegrationsTab,
  }))
)
const UserSubscriptionsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-subscriptions-tab").then(m => ({
    default: m.UserSubscriptionsTab,
  }))
)
const UserFeatureFlagsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-feature-flags-tab").then(m => ({
    default: m.UserFeatureFlagsTab,
  }))
)
const UserAccessGrantsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/user-access-grants-tab").then(m => ({
    default: m.UserAccessGrantsTab,
  }))
)

// Admin agent tabs
const AgentGeneralTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/agent/agent-general-tab").then(m => ({
    default: m.AgentGeneralTab,
  }))
)
const AgentAttributesTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/agent/agent-attributes-tab").then(m => ({
    default: m.AgentAttributesTab,
  }))
)
const AgentToolsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/agent/agent-tools-tab").then(m => ({ default: m.AgentToolsTab }))
)
const AgentPromptsTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/agent/agent-prompts-tab").then(m => ({
    default: m.AgentPromptsTab,
  }))
)
const AgentTimingTab = lazyWithRetry(() =>
  import("@/routes/admin/tabs/agent/agent-timing-tab").then(m => ({
    default: m.AgentTimingTab,
  }))
)

function SuspenseRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>
}

const trackPageViewLoader = (pageName: string): LoaderFunction => {
  return () => {
    initTracking()
    trackPageView(pageName)
    trackFbPageView()
    return null
  }
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/shared/qr/:token",
    element: (
      <SuspenseRoute>
        <SharedQrPage />
      </SuspenseRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    loader: trackPageViewLoader("Shared QR Code"),
  },
  {
    path: "/auth",
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "login",
        element: (
          <SuspenseRoute>
            <LoginRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "register",
        element: (
          <SuspenseRoute>
            <RegisterRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "forgot-password",
        element: (
          <SuspenseRoute>
            <ForgotPasswordRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "reset-password",
        element: (
          <SuspenseRoute>
            <ResetPasswordRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "email",
        element: (
          <SuspenseRoute>
            <EmailVerificationRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "google/callback",
        element: (
          <SuspenseRoute>
            <GoogleCallbackRoute />
          </SuspenseRoute>
        ),
      },
      {
        path: "facebook/callback",
        element: (
          <SuspenseRoute>
            <FacebookCallbackRoute />
          </SuspenseRoute>
        ),
      },
    ],
  },
  {
    path: "/onboarding",
    element: <OnboardingLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "",
        element: <Navigate to="company-details" replace />,
      },
      {
        path: "company-details",
        element: (
          <SuspenseRoute>
            <CompanyDetailsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Company Details"),
      },
      {
        path: "profile",
        element: (
          <SuspenseRoute>
            <OnboardingPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Profile"),
      },
      {
        path: "company",
        element: (
          <SuspenseRoute>
            <OnboardingPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Company"),
      },
      {
        path: "availability",
        element: (
          <SuspenseRoute>
            <OnboardingPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Availability"),
      },
      {
        path: "preview",
        element: (
          <SuspenseRoute>
            <OnboardingPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Preview"),
      },
      {
        path: "channels",
        element: (
          <SuspenseRoute>
            <OnboardingPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Onboarding - Channels"),
      },
    ],
  },
  {
    path: "/",
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "dashboard",
        element: (
          <SuspenseRoute>
            <DashboardRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Dashboard"),
      },
      {
        path: "dashboard/connect-whatsapp",
        element: (
          <SuspenseRoute>
            <DashboardRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Dashboard - Connect WhatsApp"),
      },
      {
        path: "calendar",
        element: (
          <SuspenseRoute>
            <CalendarRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Calendar"),
      },
      {
        path: "availability",
        element: (
          <SuspenseRoute>
            <AvailabilityRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Availability"),
      },
      {
        path: "agents",
        element: (
          <SuspenseRoute>
            <AgentsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Agents"),
      },
      {
        path: "agents/new",
        element: (
          <SuspenseRoute>
            <AgentFormRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Agents - New"),
      },
      {
        path: "agents/:id",
        element: (
          <SuspenseRoute>
            <AgentEditRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Agents - Edit"),
      },
      {
        path: "contacts",
        element: (
          <SuspenseRoute>
            <ContactsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Contacts"),
      },
      {
        path: "contacts/:id",
        element: (
          <SuspenseRoute>
            <ContactDetailRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Contacts - Detail"),
      },
      {
        path: "groups",
        element: (
          <SuspenseRoute>
            <GroupsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups"),
      },
      {
        path: "groups/team",
        element: (
          <SuspenseRoute>
            <GroupTeamRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups - Team"),
      },
      {
        path: "groups/roles",
        element: (
          <SuspenseRoute>
            <GroupRolesRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups - Roles"),
      },
      {
        path: "groups/categories",
        element: (
          <SuspenseRoute>
            <GroupCategoriesRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups - Categories"),
      },
      {
        path: "groups/metrics",
        element: (
          <SuspenseRoute>
            <GroupsMetricsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups - Metrics"),
      },
      {
        path: "groups/:id",
        element: (
          <SuspenseRoute>
            <GroupDetailRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Groups - Detail"),
      },
      {
        path: "reports",
        element: (
          <SuspenseRoute>
            <ReportsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Reports"),
      },
      {
        path: "reports-v2",
        element: (
          <SuspenseRoute>
            <ReportsV2Route />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Reports V2"),
      },
      {
        path: "chat",
        element: (
          <SuspenseRoute>
            <ChatRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Chat"),
      },
      {
        path: "chat/:id",
        element: (
          <SuspenseRoute>
            <ChatRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Chat - Detail"),
      },
      {
        path: "insights",
        element: (
          <SuspenseRoute>
            <InsightsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Insights"),
      },
      {
        path: "integrations",
        element: (
          <SuspenseRoute>
            <IntegrationsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations"),
      },
      {
        path: "integrations/google-calendar/:id",
        element: (
          <SuspenseRoute>
            <GoogleCalendarConfigPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Google Calendar Config"),
      },
      {
        path: "integrations/webhooks",
        element: (
          <SuspenseRoute>
            <WebhooksPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Webhooks"),
      },
      {
        path: "integrations/webhooks/new",
        element: (
          <SuspenseRoute>
            <WebhookNewPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Webhook New"),
      },
      {
        path: "integrations/webhooks/:id",
        element: (
          <SuspenseRoute>
            <WebhookDetailPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Webhook Detail"),
      },
      {
        path: "integrations/generic",
        element: (
          <SuspenseRoute>
            <GenericIntegrationsPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Generic"),
      },
      {
        path: "integrations/generic/new",
        element: (
          <SuspenseRoute>
            <GenericIntegrationNewPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Generic New"),
      },
      {
        path: "integrations/generic/:id",
        element: (
          <SuspenseRoute>
            <GenericIntegrationDetailPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Integrations - Generic Detail"),
      },
      {
        path: "leads",
        element: (
          <SuspenseRoute>
            <LeadsLayoutRoute />
          </SuspenseRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseRoute>
                <LeadsRoute />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Leads Pipeline"),
          },
          {
            path: "integrations",
            element: (
              <SuspenseRoute>
                <CrmIntegrationsRoute />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("CRM Integrations"),
          },
          {
            path: "reports",
            element: (
              <SuspenseRoute>
                <CrmReportsRoute />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("CRM Reports"),
          },
        ],
      },
      {
        path: "automations",
        element: (
          <SuspenseRoute>
            <AutomationsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Automations"),
      },
      {
        path: "automations/new",
        element: (
          <SuspenseRoute>
            <AutomationNewRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Automations - New"),
      },
      {
        path: "automations/new/flow",
        element: (
          <SuspenseRoute>
            <AutomationFlowRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Automations - New Flow"),
      },
      {
        path: "automations/:ruleId/flow",
        element: (
          <SuspenseRoute>
            <AutomationFlowRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Automations - Edit Flow"),
      },
      {
        path: "automations/:ruleId",
        element: (
          <SuspenseRoute>
            <AutomationEditRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Automations - Edit"),
      },
      {
        path: "media-library",
        element: (
          <SuspenseRoute>
            <MediaLibraryRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Media Library"),
      },
      {
        path: "qualification-forms",
        element: (
          <SuspenseRoute>
            <QualificationFormsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Qualification Forms"),
      },
      {
        path: "qualification-forms/:formId/edit",
        element: (
          <SuspenseRoute>
            <QualificationFormEditorRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Qualification Form Editor"),
      },
      {
        path: "qualification-forms/new",
        element: (
          <SuspenseRoute>
            <QualificationFormEditorRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Qualification Form New"),
      },
      {
        path: "attribute-groups",
        element: (
          <SuspenseRoute>
            <AttributeGroupsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Attribute Groups"),
      },
      {
        path: "attribute-groups/:groupId/edit",
        element: (
          <SuspenseRoute>
            <AttributeGroupEditorRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Attribute Group Editor"),
      },
      {
        path: "attribute-groups/new",
        element: (
          <SuspenseRoute>
            <AttributeGroupEditorRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Attribute Group New"),
      },
      {
        path: "plugins",
        element: (
          <SuspenseRoute>
            <PluginsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Plugins"),
      },
      {
        path: "plugins/public-api",
        element: (
          <SuspenseRoute>
            <PublicApiPluginRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Public API Plugin"),
      },
      {
        path: "payment-links",
        element: (
          <SuspenseRoute>
            <PaymentLinksRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Payment Links"),
      },
      {
        path: "comercial/catalogos",
        element: (
          <SuspenseRoute>
            <CommerceCatalogsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Commerce Catalogs"),
      },
      {
        path: "comercial/pedidos",
        element: (
          <SuspenseRoute>
            <CommerceOrdersRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Commerce Orders"),
      },
      {
        path: "schedulers",
        element: (
          <SuspenseRoute>
            <SchedulersRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Schedulers"),
      },
      {
        path: "broadcast",
        element: (
          <SuspenseRoute>
            <BroadcastListRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Broadcast - List"),
      },
      {
        path: "broadcast/new",
        element: (
          <SuspenseRoute>
            <BroadcastNewRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Broadcast - New"),
      },
      {
        path: "broadcast/:id/edit",
        element: (
          <SuspenseRoute>
            <BroadcastEditRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Broadcast - Edit"),
      },
      {
        path: "broadcast/:id",
        element: (
          <SuspenseRoute>
            <BroadcastDashboardRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Broadcast - Dashboard"),
      },
      // Other main routes will be added here later
    ],
  },
  {
    path: "/settings",
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "",
        element: (
          <SuspenseRoute>
            <SettingsPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Settings - General"),
      },
      {
        path: "billing",
        element: (
          <SuspenseRoute>
            <SettingsPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Settings - Billing"),
      },
      {
        path: "channels",
        element: (
          <SuspenseRoute>
            <SettingsPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Settings - Channels"),
      },
      {
        path: "attendants",
        element: (
          <SuspenseRoute>
            <SettingsPage />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Settings - Attendants"),
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <AdminRouteGuard>
        <MainLayout />
      </AdminRouteGuard>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "",
        element: (
          <SuspenseRoute>
            <AdminDashboardRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Dashboard"),
      },
      {
        path: "users",
        element: (
          <SuspenseRoute>
            <AdminUsersListRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Users List"),
      },
      {
        path: "prompts",
        element: (
          <SuspenseRoute>
            <AdminPromptsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Prompts"),
      },
      {
        path: "prompts/:templateName",
        element: (
          <SuspenseRoute>
            <AdminPromptDetailRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Prompt Detail"),
      },
      {
        path: "billing",
        element: (
          <SuspenseRoute>
            <AdminBillingRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Billing"),
      },
      {
        path: "marketing",
        element: (
          <SuspenseRoute>
            <AdminMarketingRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Marketing"),
      },
      {
        path: "settings",
        element: (
          <SuspenseRoute>
            <AdminSettingsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Settings"),
      },
      {
        path: "conversations",
        element: (
          <SuspenseRoute>
            <AdminConversationsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Conversations"),
      },
      {
        path: "conversations/contact/:contactId",
        element: (
          <SuspenseRoute>
            <AdminConversationTimelineRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Conversation Timeline"),
      },
      {
        path: "tech-providers",
        element: (
          <SuspenseRoute>
            <AdminTechProvidersRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Tech Providers"),
      },
      {
        path: "library",
        element: (
          <SuspenseRoute>
            <AdminLibraryRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Library"),
      },
      {
        path: "llm-models",
        element: (
          <SuspenseRoute>
            <AdminLLMModelsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - LLM Models"),
      },
      {
        path: "tools",
        element: (
          <SuspenseRoute>
            <AdminToolsRoute />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Tools"),
      },
      {
        path: "users/:id",
        element: (
          <SuspenseRoute>
            <UserDetailLayout />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - User Detail"),
        children: [
          {
            path: "",
            element: <Navigate to="overview" replace />,
          },
          {
            path: "overview",
            element: (
              <SuspenseRoute>
                <UserOverviewTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Overview"),
          },
          {
            path: "agents",
            element: (
              <SuspenseRoute>
                <UserAgentsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Agents"),
          },
          {
            path: "leads",
            element: (
              <SuspenseRoute>
                <UserLeadsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Leads"),
          },
          {
            path: "appointments",
            element: (
              <SuspenseRoute>
                <UserAppointmentsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Appointments"),
          },
          {
            path: "providers",
            element: (
              <SuspenseRoute>
                <UserProvidersTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Providers & Schedule"),
          },
          {
            path: "communication",
            element: (
              <SuspenseRoute>
                <UserCommunicationTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Communication"),
          },
          {
            path: "activities",
            element: (
              <SuspenseRoute>
                <UserActivitiesTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Activities"),
          },
          {
            path: "integrations",
            element: (
              <SuspenseRoute>
                <UserIntegrationsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Integrations"),
          },
          {
            path: "subscriptions",
            element: (
              <SuspenseRoute>
                <UserSubscriptionsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Subscriptions"),
          },
          {
            path: "feature-flags",
            element: (
              <SuspenseRoute>
                <UserFeatureFlagsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Feature Flags"),
          },
          {
            path: "access-grants",
            element: (
              <SuspenseRoute>
                <UserAccessGrantsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - User Access Grants"),
          },
        ],
      },
      {
        path: "users/:userId/agents/:agentId",
        element: (
          <SuspenseRoute>
            <AgentDetailLayout />
          </SuspenseRoute>
        ),
        loader: trackPageViewLoader("Admin - Agent Detail"),
        children: [
          {
            index: true,
            element: <Navigate to="general" replace />,
          },
          {
            path: "general",
            element: (
              <SuspenseRoute>
                <AgentGeneralTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - Agent General"),
          },
          {
            path: "attributes",
            element: (
              <SuspenseRoute>
                <AgentAttributesTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - Agent Attributes"),
          },
          {
            path: "tools",
            element: (
              <SuspenseRoute>
                <AgentToolsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - Agent Tools"),
          },
          {
            path: "prompts",
            element: (
              <SuspenseRoute>
                <AgentPromptsTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - Agent Prompts"),
          },
          {
            path: "composition",
            element: <Navigate to="../prompts" replace />,
          },
          {
            path: "timing",
            element: (
              <SuspenseRoute>
                <AgentTimingTab />
              </SuspenseRoute>
            ),
            loader: trackPageViewLoader("Admin - Agent Timing"),
          },
        ],
      },
    ],
  },
  {
    path: "/auth/instagram/callback",
    element: (
      <SuspenseRoute>
        <InstagramCallbackRoute />
      </SuspenseRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/external-calendar/oauth/google/callback",
    element: (
      <SuspenseRoute>
        <GoogleCalendarCallbackRoute />
      </SuspenseRoute>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "*",
    element: <RouteErrorBoundary />,
  },
])
